import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useAuth } from "react-oidc-context";
import { toast } from "sonner";
import {
  PresenceTableName,
  useDynamoDeleteItem,
  useDynamoMutateItem,
} from "../services/dynamo";
import {
  askUserPermission,
  createNotificationSubscription,
  getUserSubscription,
  isPushNotificationSupported,
} from "../services/push-notifications"; // Cleaned imports

/**
 * Custom React hook per la gestione delle notifiche push.
 * Fornisce funzioni e stato per:
 * - Verificare il supporto e lo stato dei permessi delle notifiche push
 * - Gestire la sottoscrizione e la cancellazione delle notifiche push
 * - Sincronizzare la sottoscrizione con il backend DynamoDB
 *
 * @returns {object} Oggetto con stato e metodi per notifiche push
 *   - isSupported: boolean, se il browser supporta le notifiche push
 *   - permissionStatus: stato attuale dei permessi delle notifiche push
 *   - subscriptionSaveMutation: mutazione per salvare la sottoscrizione su DynamoDB
 *   - subscriptionMutation: mutazione per creare/salvare la sottoscrizione
 *   - subscriptionStatus: stato della sottoscrizione attuale
 *   - unsubscriptionMutation: mutazione per cancellare la sottoscrizione
 */
export const usePushNotifications = () => {
  // Recupera l'utente autenticato dal contesto OIDC
  const { user } = useAuth();
  // Verifica se il browser supporta le notifiche push
  const isSupported = isPushNotificationSupported();
  // Stato attuale dei permessi delle notifiche push ("default", "granted", "denied")
  const permissionStatus = Notification.permission;

  // La registrazione manuale del service worker è gestita da VitePWA

  /**
   * Query per recuperare la sottoscrizione push attuale dell'utente dal browser.
   * Aggiornata automaticamente quando cambia lo stato della sottoscrizione.
   */
  const subscriptionStatus = useQuery({
    queryKey: ["push-subscription"],
    queryFn: async () => getUserSubscription(),
  });

  /**
   * Mutazione per salvare la sottoscrizione push su DynamoDB.
   * Utilizza un item con chiave primaria pk (user id) e sk (endpoint della sottoscrizione).
   */
  const subscriptionSaveMutation = useDynamoMutateItem<{
    pk: string;
    sk: PushSubscription["endpoint"];
    tk: "subscription";
    subscriptionData: PushSubscriptionJSON;
    exp?: number | null;
  }>(PresenceTableName, {
    mutationKey: ["dynamo", PresenceTableName, "push-subscription-save"],
  });

  /**
   * Mutazione per creare una nuova sottoscrizione push e salvarla su DynamoDB.
   * 1. Chiede il permesso all'utente se necessario
   * 2. Crea la sottoscrizione tramite il service worker
   * 3. Salva la sottoscrizione su DynamoDB
   * 4. Aggiorna lo stato della query
   */
  const subscriptionMutation = useMutation({
    mutationFn: async () => {
      if (Notification.permission !== "granted") {
        await askUserPermission();
      }
      // Chiede il permesso e crea la sottoscrizione push

      if (Notification.permission !== "granted") {
        throw new Error("Permission denied");
      }
      const sub = await createNotificationSubscription();

      // Salva la sottoscrizione su DynamoDB se presente e l'utente è autenticato
      if (sub && user?.profile.sub) {
        await subscriptionSaveMutation.mutateAsync({
          pk: `${user?.profile.sub}`,
          sk: `subscription|${sub.endpoint}`,
          tk: "subscription",
          subscriptionData: sub.toJSON(),
          exp: !!sub.expirationTime
            ? Math.floor(sub.expirationTime + Date.now() / 1000)
            : undefined,
        });
      }
      return sub;
    },
    onSuccess: () => {
      toast.success("Notifiche attivate");
    },
    onError: () => {
      toast.error("Impossibile attivare le notifiche");
    },
    onSettled: () => {
      subscriptionStatus.refetch();
    },
  });

  /**
   * Mutazione per cancellare la sottoscrizione push da DynamoDB.
   * Utilizza la chiave primaria dell'utente e l'endpoint della sottoscrizione.
   */
  const unsubscriptionSaveMutation = useDynamoDeleteItem(PresenceTableName, {
    mutationKey: ["dynamo", PresenceTableName, "push-subscription-delete"],
  });

  /**
   * Mutazione per annullare la sottoscrizione push:
   * 1. Cancella la sottoscrizione dal backend DynamoDB
   * 2. Chiama unsubscribe() sulla sottoscrizione del browser
   * 3. Aggiorna lo stato della query
   */
  const unsubscriptionMutation = useMutation({
    mutationFn: async () => {
      // Recupera la sottoscrizione attuale e la cancella sia dal backend che dal browser
      const subscription = subscriptionStatus.data;
      if (subscription) {
        await unsubscriptionSaveMutation.mutateAsync({
          key: {
            pk: `${user?.profile.sub}`,
            sk: `subscription|${subscription.endpoint}`,
          },
        });

        await subscription.unsubscribe();
      }
    },
    onSuccess: () => {
      toast.success("Notifiche disattivate");
    },
    onError: () => {
      toast.error("Impossibile disattivare le notifiche");
    },
    onSettled: () => {
      subscriptionStatus.refetch();
    },
  });

  // Memoizza e restituisce tutte le funzioni e stati utili per la gestione delle notifiche push
  return useMemo(
    () => ({
      isSupported,
      permissionStatus,
      subscriptionSaveMutation,
      subscriptionMutation,
      subscriptionStatus,
      unsubscriptionMutation,
    }),
    [
      isSupported,
      permissionStatus,
      subscriptionSaveMutation,
      subscriptionMutation,
      subscriptionStatus,
      unsubscriptionMutation,
    ],
  );
};
